import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import { formatHistoryDateTime } from '../utils/visaApplicationHistoryApi';
import { formatStatusLabel, getStatusBadgeClass } from '../utils/visaApplicationStatus';
import { getFormDisplayEntries } from '../utils/visaApplicationData';

const VisaApplicationHistoryModal = ({ application, historyUrl, onClose }) => {
  const [history, setHistory] = useState([]);
  const [formVersions, setFormVersions] = useState([]);
  const [currentFormVersion, setCurrentFormVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('status');
  const [viewingVersion, setViewingVersion] = useState(null);

  useEffect(() => {
    if (!application?.id || !historyUrl) return;

    let cancelled = false;

    const fetchHistory = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(historyUrl);
        if (!cancelled) {
          setHistory(res.data.history || []);
          setFormVersions(res.data.form_versions || []);
          setCurrentFormVersion(Number(res.data.current_form_version || 0));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load status history');
          setHistory([]);
          setFormVersions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchHistory();

    return () => {
      cancelled = true;
    };
  }, [application?.id, historyUrl]);

  const versionFormEntries = useMemo(() => {
    if (!viewingVersion?.form_data) return [];
    return getFormDisplayEntries(viewingVersion.form_data);
  }, [viewingVersion]);

  if (!application) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {viewingVersion ? `Form ${viewingVersion.label}` : 'Application History'}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Passenger ID: {application.passenger_id}
              {currentFormVersion > 0 ? ` · Current form v${currentFormVersion}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (viewingVersion) {
                setViewingVersion(null);
                return;
              }
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {loading ? (
          <LoadingSpinner text="Loading history..." />
        ) : error ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        ) : viewingVersion ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                  {viewingVersion.label}
                </span>
                <p className="text-xs text-gray-500 mt-2">
                  Saved {formatHistoryDateTime(viewingVersion.created_at)}
                  {viewingVersion.changed_by_name
                    ? ` by ${viewingVersion.changed_by_name}`
                    : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewingVersion(null)}
                className="text-sm px-3 py-1.5 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Back to versions
              </button>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl divide-y divide-gray-200">
              {versionFormEntries.length > 0 ? (
                versionFormEntries.map((entry) => (
                  <div key={entry.key} className="px-4 py-3 grid grid-cols-1 sm:grid-cols-3 gap-1">
                    <span className="font-medium text-gray-600">{entry.label}</span>
                    <span className="sm:col-span-2 text-gray-900 break-words">{entry.value}</span>
                  </div>
                ))
              ) : (
                <p className="px-4 py-3 text-gray-500">No form field data in this version.</p>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-4 border-b border-gray-100 pb-3">
              <button
                type="button"
                onClick={() => setTab('status')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  tab === 'status'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Status History
              </button>
              <button
                type="button"
                onClick={() => setTab('forms')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  tab === 'forms'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Form Versions{formVersions.length ? ` (${formVersions.length})` : ''}
              </button>
            </div>

            {tab === 'status' ? (
              history.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No status history recorded yet.</p>
              ) : (
                <div className="space-y-4">
                  {history.map((entry, index) => (
                    <div
                      key={entry.id || index}
                      className="border border-gray-100 rounded-xl p-4 bg-gray-50/50"
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(entry.application_status)}`}
                        >
                          {formatStatusLabel(entry.application_status)}
                        </span>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatHistoryDateTime(entry.created_at)}
                        </span>
                      </div>
                      {entry.status_description && (
                        <p className="text-sm text-gray-700 mt-3">{entry.status_description}</p>
                      )}
                      {entry.changed_by_name && (
                        <p className="text-xs text-gray-400 mt-2">
                          Updated by {entry.changed_by_name}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : formVersions.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No form versions yet.</p>
            ) : (
              <div className="space-y-3">
                {formVersions.map((version) => (
                  <div
                    key={version.id || version.version}
                    className="border border-gray-100 rounded-xl p-4 bg-gray-50/50 flex items-center justify-between gap-3 flex-wrap"
                  >
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">
                          {version.label}
                        </span>
                        {Number(version.version) === Number(currentFormVersion) && (
                          <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {formatHistoryDateTime(version.created_at)}
                        {version.changed_by_name ? ` · ${version.changed_by_name}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setViewingVersion(version)}
                      className="text-sm px-3 py-1.5 rounded-lg font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                      View Form
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VisaApplicationHistoryModal;
